import { useEffect, useState } from 'react';
import { getLimits } from './api';
import { FALLBACK_LIMITS, apiConfigured } from '../config/env';
import type { ServiceLimits } from './types';

/**
 * The service limits, fetched once per page load.
 *
 * The caps are a deliberate design decision rather than an accident, so they
 * are shown to the user rather than hidden: 5000 documents and 2000 map items
 * keep a job inside the synchronous request window. The API is the authority;
 * the compiled-in fallback only covers the moment before it answers and the
 * case where it cannot be reached.
 *
 * They come from `GET /health`, which is the only place this service publishes
 * them -- `/v1/limits` does not exist -- and where the API's own comment
 * records why every cap the UI states has to come from the server. Note that
 * the item cap is called `max_map_items` there; `getLimits` renames it, and
 * before it did, the UI's item cap was `undefined`.
 */
const FALLBACK: ServiceLimits = {
  max_documents: FALLBACK_LIMITS.maxDocuments,
  max_items: FALLBACK_LIMITS.maxItems,
  max_upload_bytes: FALLBACK_LIMITS.maxUploadBytes,
};

let cached: ServiceLimits | null = null;

export function useLimits(): ServiceLimits {
  const [limits, setLimits] = useState<ServiceLimits>(cached ?? FALLBACK);

  useEffect(() => {
    if (cached || !apiConfigured) return;
    const controller = new AbortController();
    getLimits(controller.signal)
      .then((next) => {
        cached = next;
        setLimits(next);
      })
      .catch(() => {
        // The fallback stands. A failure here is not worth an error banner:
        // the real limit is enforced server-side and will be reported then.
        //
        // A malformed answer lands here too, not in the branch above: the
        // mapper rejects a health response that is missing a cap rather than
        // reporting it as zero, because a cap of 0 would disable the form
        // while the fallback keeps it usable.
      });
    return () => controller.abort();
  }, []);

  return limits;
}
